/**
 * liteMenuVisibilityStore — Super Admin can toggle which items appear in the
 * Lite storefront left-side drawer ("Menu" panel opened by the hamburger).
 *
 * Persisted to `khays_lite_menu_visibility` and synced via cloudSync (the
 * key is in SYNC_KEYS and on the public-readable RLS allow-list).
 *
 * Default = visible. Hidden items disappear for every visitor on the Lite
 * storefront.
 */
import { create } from "zustand";

const KEY = "khays_lite_menu_visibility";

export type LiteMenuItemId =
  // Tabs surfaced in the drawer
  | "home" | "available" | "offers" | "favorites" | "cart" | "profile"
  // Quick links section
  | "track" | "swap" | "admin"
  // Section headers
  | "section_quicklinks";

export const LITE_MENU_GROUPS: { group: string; items: { id: LiteMenuItemId; label: string }[] }[] = [
  {
    group: "Navigation",
    items: [
      { id: "home", label: "Home" },
      { id: "available", label: "Available Now" },
      { id: "offers", label: "Special Offers" },
      { id: "favorites", label: "Favorites" },
      { id: "cart", label: "Cart" },
      { id: "profile", label: "Profile" },
    ],
  },
  {
    group: "Quick links",
    items: [
      { id: "section_quicklinks", label: "Quick links section" },
      { id: "track", label: "Track Order" },
      { id: "swap", label: "Swap Device" },
      { id: "admin", label: "Admin" },
    ],
  },
];

export type LiteMenuVisibilityConfig = Record<string, boolean>;

function load(): LiteMenuVisibilityConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

function persist(c: LiteMenuVisibilityConfig) {
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch {}
}

interface State {
  config: LiteMenuVisibilityConfig;
  _rev: number;
  isVisible: (id: LiteMenuItemId) => boolean;
  setVisible: (id: LiteMenuItemId, v: boolean) => void;
  reset: () => void;
  reload: () => void;
}

export const useLiteMenuVisibility = create<State>((set, get) => ({
  config: load(),
  _rev: 0,
  isVisible: (id) => get().config[id] !== false,
  setVisible: (id, v) => {
    const next = { ...get().config, [id]: v };
    persist(next);
    set({ config: next, _rev: get()._rev + 1 });
  },
  reset: () => {
    try { localStorage.removeItem(KEY); } catch {}
    set({ config: {}, _rev: get()._rev + 1 });
  },
  reload: () => set({ config: load(), _rev: get()._rev + 1 }),
}));
